import type {
  ChapterBoundary,
  ChapterizationResult,
} from './chapterizationResponse';
import type { ReelChapter } from './reelTypes';

/**
 * PLAT-11154 — what a subset run is allowed to have changed (S3, S7).
 *
 * The creator ticked some Sections; everything else is **fixed**. A fixed
 * Section keeps its position, stays in the chapter it is in, and that chapter
 * keeps its name. This module is where that is guaranteed rather than requested:
 * the model is told the rule (PLAT-11152's prompt), and a response that breaks it
 * is corrected here.
 *
 * Corrected **on the offending point only** (S7). A run that moves one Section it
 * was not allowed to move is 90% useful, and throwing the whole thing away costs
 * the creator a regeneration to punish a model.
 *
 * The precise rule, because "keeps its chapter" has a loose reading that lets a
 * chapter change under a Section nobody ticked. Let **F** be the set of
 * *positions* the fixed Sections occupy:
 *
 * 1. Every position in F keeps the Section that is in it, and the ticked
 *    Sections may only be permuted among the positions ticked Sections already
 *    occupy.
 * 2. For every position in F, the boundary that opens its chapter is unchanged —
 *    the greatest boundary position at or below it is the same before and after,
 *    and that chapter keeps its title and `titleSource`.
 *
 * Rule 2 is the one that is easy to get wrong. Removing a boundary *above* a
 * fixed Section merges it into the previous chapter; adding one *at* it makes it
 * open a chapter of its own. Both change the chapter of a Section the creator
 * protected, and neither is a move.
 *
 * What falls out is the intuition the field is sold on: **the AI may carve new
 * chapters out of runs of ticked Sections, and may do nothing else to the
 * structure.**
 *
 * Pure — no React, no store access, no network.
 */

export type ChapterizationConstraints = {
  /** The reel now, in playback order. */
  currentSectionIds: readonly string[];
  /** The reel's chapters now, for the titles rule 2 protects. */
  currentChapters: readonly ReelChapter[];
  /** The Sections the creator ticked. Everything else is fixed. */
  movableSectionIds: ReadonlySet<string>;
};

export type ConstrainedChapterization = ChapterizationResult & {
  /**
   * PLAT-11154 S6 — titles this run carried over rather than wrote, keyed by
   * the Section their chapter opens at, with the provenance they had. A subset
   * run edits chapters in place, so DE10 applies literally: recording a title
   * the creator typed as `'generated'` would quietly make it replaceable by the
   * next generation that came along.
   *
   * Reported from here rather than re-derived by the caller, because *this* is
   * where the answer is known. Matching a finished boundary back to an existing
   * chapter by its title text — the shape this started as — cannot tell a
   * carried-over title from a generated one that happens to read the same, and
   * a reel with two chapters called "Q&A" is not exotic.
   */
  preservedTitleSources: Map<string, ReelChapter['titleSource']>;
};

/**
 * @returns the corrected order and boundaries, or an empty result when nothing
 *   the model proposed survived — in which case the caller fails the run and
 *   writes nothing
 */
export function constrainChapterization(
  proposed: ChapterizationResult,
  constraints: ChapterizationConstraints,
): ConstrainedChapterization {
  const { currentSectionIds, currentChapters, movableSectionIds } = constraints;
  const total = currentSectionIds.length;
  if (total === 0) return NOTHING_PRESERVED;

  const isMovable = (id: string) => movableSectionIds.has(id);
  const fixedPositions: number[] = [];
  const movablePositions: number[] = [];
  currentSectionIds.forEach((id, position) => {
    (isMovable(id) ? movablePositions : fixedPositions).push(position);
  });

  // --- Rule 1: the order -----------------------------------------------------
  // A fixed Section is put back at its own position, and the ticked ones re-flow
  // around it through the positions ticked Sections already held. Expressed as
  // "fill the movable slots in the order the model asked for" rather than as a
  // check, so a placement into a fixed position cannot be produced in the first
  // place.
  const queue = proposed.orderedSectionIds.filter(isMovable);
  const queued = new Set(queue);
  // A ticked Section the response forgot keeps its own relative order at the
  // back, the same rule PLAT-11153 S6 applies to the whole reel.
  for (const id of currentSectionIds) {
    if (isMovable(id) && !queued.has(id)) queue.push(id);
  }

  const orderedSectionIds = [...currentSectionIds];
  movablePositions.forEach((position, index) => {
    orderedSectionIds[position] = queue[index];
  });

  const positionOf = new Map(orderedSectionIds.map((id, index) => [id, index]));

  // --- Rule 2: the boundaries the fixed Sections depend on -------------------
  const currentBoundaries = boundaryPositions(
    currentChapters,
    currentSectionIds,
  );
  const openingBefore = (position: number) =>
    greatestAtOrBelow(currentBoundaries, position);

  // Rule 2 protects the chapter a fixed Section is *in*, so it has nothing to
  // say about a reel that has no chapters yet: there is no membership to
  // preserve, and applying it anyway would forbid every boundary above every
  // unticked Section — which on a flat reel is almost all of them, leaving a
  // subset run able to cut only after the last fixed Section. E8's promise is
  // that a subset run preserves what the creator built; on a flat reel they
  // have built nothing, so only rule 1 applies.
  const hasChapters = currentChapters.length > 0;

  // Every boundary that currently opens a fixed Section's chapter has to remain
  // one, or that Section merges into the chapter above it.
  const required = new Set(
    hasChapters ? fixedPositions.map(openingBefore) : [],
  );
  // And no boundary may appear between a fixed Section and the boundary that
  // opens it, or that Section starts a different chapter than it did.
  const forbidden = new Set<number>();
  if (hasChapters) {
    for (const position of fixedPositions) {
      for (let q = openingBefore(position) + 1; q <= position; q += 1) {
        forbidden.add(q);
      }
    }
  }

  // --- The proposal, filtered ------------------------------------------------
  let survived = 0;
  const proposedAt = new Map<number, string | undefined>();
  for (const group of proposed.groups) {
    const positions = group.sectionIds
      .map(id => positionOf.get(id))
      .filter((position): position is number => position !== undefined)
      .sort((a, b) => a - b);
    if (positions.length === 0) continue;

    // A chapter is a run of consecutive Sections (DE12). Once the fixed ones
    // are back in place a proposed grouping may straddle them, and a boundary
    // taken from it would claim Sections the model never put in it.
    const isConsecutive = positions.every(
      (position, index) => position === positions[0] + index,
    );
    if (!isConsecutive) continue;

    const opensAt = positions[0];
    if (forbidden.has(opensAt)) continue;

    proposedAt.set(opensAt, group.title);
    survived += 1;
  }

  const orderChanged = orderedSectionIds.some(
    (id, index) => id !== currentSectionIds[index],
  );
  // S7's last row: nothing the model proposed got through, and it moved nothing
  // either. There is no edit left to apply, and reporting a run that did nothing
  // as a success is worse than reporting it as a failure.
  if (survived === 0 && !orderChanged) {
    return NOTHING_PRESERVED;
  }

  const finalPositions = [
    ...new Set<number>([0, ...required, ...proposedAt.keys()]),
  ].sort((a, b) => a - b);

  // --- Titles ----------------------------------------------------------------
  // A chapter the creator protected keeps the name it had, and so does one they
  // typed — DE10 applies literally here, because a subset run edits chapters in
  // place rather than destroying them under a stated confirmation.
  const currentTitleAt = new Map<number, ReelChapter>();
  for (const chapter of currentChapters) {
    const position = currentSectionIds.indexOf(chapter.firstSectionId);
    if (position !== -1 && !currentTitleAt.has(position)) {
      currentTitleAt.set(position, chapter);
    }
  }

  // Recorded as each boundary is built, because this is the only point at which
  // "this title was carried over" and "this title was written by the run" are
  // still distinguishable — by the time there is a finished boundary, the two
  // are both just strings.
  const preservedTitleSources = new Map<string, ReelChapter['titleSource']>();

  const boundaries: ChapterBoundary[] = finalPositions.map(position => {
    const firstSectionId = orderedSectionIds[position];
    const existing = currentTitleAt.get(position);
    const keepsExisting =
      existing?.title &&
      (required.has(position) || existing.titleSource === 'user');
    if (keepsExisting) {
      preservedTitleSources.set(firstSectionId, existing.titleSource);
      return { firstSectionId, title: existing.title };
    }
    const proposedTitle = proposedAt.get(position);
    if (!proposedTitle && existing?.title) {
      // A boundary that survives with no title of its own keeps the one that
      // was there rather than taking a name written for a different set of
      // Sections — and it keeps that name's provenance with it.
      preservedTitleSources.set(firstSectionId, existing.titleSource);
      return { firstSectionId, title: existing.title };
    }
    return {
      firstSectionId,
      ...(proposedTitle ? { title: proposedTitle } : {}),
    };
  });

  return {
    boundaries,
    groups: groupsFrom(finalPositions, orderedSectionIds, boundaries),
    orderedSectionIds,
    preservedTitleSources,
  };
}

/** Nothing the model proposed survived; the caller fails the run. */
const NOTHING_PRESERVED: ConstrainedChapterization = {
  boundaries: [],
  groups: [],
  orderedSectionIds: [],
  preservedTitleSources: new Map(),
};

/** Chapter boundaries as positions in the reel, deduped and in order. */
function boundaryPositions(
  chapters: readonly ReelChapter[],
  sectionIds: readonly string[],
): number[] {
  const positions = new Set<number>([0]);
  for (const chapter of chapters) {
    const position = sectionIds.indexOf(chapter.firstSectionId);
    if (position !== -1) positions.add(position);
  }
  return [...positions].sort((a, b) => a - b);
}

/** The greatest boundary at or below `position`; 0 when there is none. */
function greatestAtOrBelow(
  boundaries: readonly number[],
  position: number,
): number {
  let found = 0;
  for (const boundary of boundaries) {
    if (boundary > position) break;
    found = boundary;
  }
  return found;
}

function groupsFrom(
  positions: readonly number[],
  orderedSectionIds: readonly string[],
  boundaries: readonly ChapterBoundary[],
) {
  return positions.map((position, index) => ({
    ...(boundaries[index].title ? { title: boundaries[index].title } : {}),
    sectionIds: orderedSectionIds.slice(
      position,
      positions[index + 1] ?? orderedSectionIds.length,
    ),
  }));
}
